import type { ClubMemberRole } from '../../api';
import type { User } from '../../types';

export const roleLabel = (
  role?: string,
  pendingRole?: string | null
): string => {
  if (role === 'owner') {
    return 'Владелец клуба';
  }
  if (role === 'admin') return 'Админ';
  if (role === 'moderator') return 'Модератор';
  if (role === 'technician') return 'Техник';
  if (role === 'guest') return 'Гость';
  if (role === 'inactive') return 'Неактивен';
  if (role === 'pending') {
    const target =
      pendingRole === 'admin'
        ? 'Админ'
        : pendingRole === 'owner'
          ? 'Владелец'
          : 'Пилот';
    return `Ожидание (${target})`;
  }
  return 'Пилот';
};

export const OWNER_STATUS_OPTIONS = [
  { value: '1', label: 'Владелец — пилот' },
  { value: '0', label: 'Владелец — не пилот' },
] as const;

/** Кто может удалить / сменить роль участника. */
export const canManageMember = (
  actorClubRole: string | null | undefined,
  target: { id: number; role?: string; pending_role?: string | null },
  currentUserId: number | null
) => {
  if (!currentUserId || target.id === currentUserId) return false;
  const targetRole = target.role || 'pilot';
  if (targetRole === 'owner' || targetRole === 'inactive') return false;
  const effective =
    targetRole === 'pending'
      ? target.pending_role || 'pilot'
      : targetRole;
  if (effective === 'admin' || effective === 'moderator') {
    return actorClubRole === 'owner';
  }
  return (
    actorClubRole === 'owner' ||
    actorClubRole === 'admin' ||
    actorClubRole === 'moderator'
  );
};

const ASSIGNABLE_ROLES: { value: ClubMemberRole; label: string }[] = [
  { value: 'owner', label: 'Владелец' },
  { value: 'admin', label: 'Админ' },
  { value: 'moderator', label: 'Модератор' },
  { value: 'pilot', label: 'Пилот' },
  { value: 'technician', label: 'Техник' },
  { value: 'guest', label: 'Гость' },
];

export const assignableRolesFor = (actorClubRole: string | null | undefined) => {
  if (actorClubRole === 'owner') return ASSIGNABLE_ROLES;
  return ASSIGNABLE_ROLES.filter(
    (r) =>
      r.value !== 'admin' &&
      r.value !== 'moderator' &&
      r.value !== 'owner'
  );
};

const ROLE_SORT_ORDER: Record<string, number> = {
  owner: 0,
  admin: 1,
  moderator: 2,
  pilot: 3,
  pending: 4,
  technician: 5,
  guest: 6,
  inactive: 7,
};

export const sortMembersByRole = (list: User[]) =>
  [...list].sort((a, b) => {
    const byRole =
      (ROLE_SORT_ORDER[a.role || 'pilot'] ?? 99) -
      (ROLE_SORT_ORDER[b.role || 'pilot'] ?? 99);
    if (byRole !== 0) return byRole;
    return (a.name || '').localeCompare(b.name || '', 'ru');
  });

export const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Полная дата YYYY-MM-DD с реалистичным годом (не «0002» при ручном вводе). */
export const isPlausibleIsoDate = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  if (year < 1900 || year > 2100) return false;
  const t = Date.parse(`${value}T00:00:00`);
  return !Number.isNaN(t);
};

export const emptyAddForm = () => ({
  name: '',
  login: '',
  password: '',
  role: 'pilot' as ClubMemberRole,
  joined_on: todayIso(),
});
